import { useAxios, useStateReset, useUpdateEffect } from "@Tmp/Hooks";
import useKeyPress from "@Tmp/Hooks/useKeyPress";
import { Product } from "@TmpApp/types";
import clsx from "clsx";
import * as React from "react";
import { IProductsLiveSearchProps, TProductsLiveSearchSelectedItem } from ".";
import Input, { InputImageIcon } from "../Input";
import { ProductsLiveSearchMenu } from "./ProductsLiveSearchMenu";

const ProductsLiveSearch: React.FC<IProductsLiveSearchProps> = ({
    value,
    onChange,
    onSelect,
    onEnter,
    Input: { onChange: InputOnChange, ...InputProps },
    disableRelativeMenu = false,
    listItemsAmount = 5,
    disablePrices = false,
    showInventory = false,
    ...props
}) => {
    const [open, setOpen] = React.useState(false);
    const [focused, setFocused] = React.useState(false);
    const [items, setItems] = React.useState<Product[]>([]);
    const [liveSearchLoading, setLiveSearchLoading] = React.useState(false);
    const [currentProduct, setCurrentProduct] = React.useState<null | Product>(null);
    const [selectedItem, setSelectedItem, resetSelectedItem] =
        useStateReset<TProductsLiveSearchSelectedItem>({
            index: null,
            product: null,
        });
    const inputRef = React.useRef(null);

    const handleChange = (value: string) => {
        onChange(value);
    };

    const setSelectedItemByIndex = (index: number) => {
        let indexValue = index;
        if (index < 0) {
            indexValue = 0;
        } else if (index >= listItemsAmount - 1) {
            indexValue = listItemsAmount - 1;
        }

        const product = items?.[indexValue];

        setSelectedItem((prev) => {
            const newValue = { index: indexValue, product: product };

            if (!newValue.product && prev.product) {
                return prev;
            }

            return newValue;
        });
    };

    useKeyPress(["ArrowUp", "ArrowDown", "Enter"], {
        callback: (keys) => {
            if (keys.some((key) => key === "Enter")) {
                if (open && selectedItem?.product) {
                    setCurrentProduct(selectedItem.product);
                    onChange(selectedItem.product.sku);
                    resetSelectedItem();
                    setOpen(false);
                } else {
                    onEnter?.();
                }
            } else if (keys.some((key) => key === "ArrowUp")) {
                setSelectedItemByIndex((selectedItem.index ?? 0) - 1);
            } else if (keys.some((key) => key === "ArrowDown")) {
                setOpen(true);
                setSelectedItemByIndex((selectedItem.index ?? -1) + 1);
            }
        },
        listenerElement: inputRef.current,
    });

    useUpdateEffect(() => {
        setOpen(true);
        setCurrentProduct(null);
        setLiveSearchLoading(true);
    }, [value]);

    const { status: fetchStatus, value: fetchValue } = useAxios(
        "get",
        "/wp-content/plugins/tmp/api/products.php",
        { q: value, perpage: listItemsAmount, "p-page": 1, sku: value },
        [value],
        { debounceDelay: 200 }
    );

    React.useEffect(() => {
        if (fetchStatus === "success" && fetchValue.data.items) {
            setItems(fetchValue.data.items);
            resetSelectedItem();
            if (
                fetchValue.data.product &&
                value.toLowerCase() === fetchValue.data.product?.sku?.toLowerCase?.()
            ) {
                setCurrentProduct(fetchValue.data.product);
            }
        }
        if (fetchStatus !== "loading") {
            setLiveSearchLoading(false);
        } else {
            setLiveSearchLoading(true);
        }
    }, [fetchStatus]);

    const handleFocus: React.FocusEventHandler<HTMLInputElement> = () => {
        setOpen(true);
        setFocused(true);
    };

    const handleBlur: React.FocusEventHandler<HTMLInputElement> = () => {
        setFocused(false);
    };

    const inputWrapperRef = React.useRef(null);

    return (
        <div className={clsx({ "tmp-relative": !disableRelativeMenu })}>
            <Input
                {...InputProps}
                label={currentProduct?.name}
                input={{
                    onFocus: handleFocus,
                    onBlur: handleBlur,
                    ref: inputRef,
                    ...InputProps?.input,
                }}
                wrapper={{
                    ref: inputWrapperRef,
                    ...InputProps?.wrapper,
                }}
                iconBefore={
                    currentProduct?.has_image ? (
                        <InputImageIcon src={currentProduct.image} />
                    ) : (
                        "far fa-image"
                    )
                }
                value={value}
                onChange={(e) => {
                    handleChange(e.target.value);
                    InputOnChange?.(e);
                }}
            />
            <ProductsLiveSearchMenu
                focused={focused}
                loading={liveSearchLoading}
                items={items}
                open={open}
                setOpen={setOpen}
                value={value}
                onChange={handleChange}
                onSelect={onSelect}
                setCurrentProduct={setCurrentProduct}
                inputWrapperRef={inputWrapperRef}
                selectedItem={selectedItem}
                disablePrices={disablePrices}
                showInventory={showInventory}
            />
        </div>
    );
};

export default ProductsLiveSearch;
